import { useCallback, useEffect, useMemo, useState } from "react";
import { Minus, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatApiError } from "../lib/api";
import { localizeCategory, localizeFoodName, localizeServing } from "../lib/foodTranslations";
import { supabase } from "../lib/supabase";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const DAILY_LIMITS = {
  potassium: 2000,
  phosphorus: 900,
  sodium: 2300,
};

const today = () => new Date().toISOString().slice(0, 10);
const todayRange = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(end.getDate() + 1);

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
};
const logDate = (log) => (log.logged_at || new Date().toISOString()).slice(0, 10);
const numberValue = (value) => Number(value || 0);
const nutrientValue = (food, key) => numberValue(food[key]);
const nutrientPayload = (food, quantity) => ({
  potassium_mg: nutrientValue(food || {}, "potassium_mg") * quantity,
  phosphorus_mg: nutrientValue(food || {}, "phosphorus_mg") * quantity,
  sodium_mg: nutrientValue(food || {}, "sodium_mg") * quantity,
  protein_g: nutrientValue(food || {}, "protein_g") * quantity,
});
const readGuestFoodLogs = () => {
  if (typeof window === "undefined") return [];

  try {
    const savedLogs = localStorage.getItem("guestFoodLogs");
    const parsedLogs = savedLogs ? JSON.parse(savedLogs) : [];
    return Array.isArray(parsedLogs) ? parsedLogs : [];
  } catch {
    localStorage.removeItem("guestFoodLogs");
    return [];
  }
};
const saveGuestFoodLogs = (logs) => {
  localStorage.setItem("guestFoodLogs", JSON.stringify(logs));
};

const riskClass = (value, limit) => {
  if (!limit) return "text-foreground";
  const percent = (numberValue(value) / limit) * 100;
  if (percent <= 33) return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (percent <= 66) return "text-amber-700 bg-amber-50 border-amber-200";
  return "text-red-700 bg-red-50 border-red-200";
};

const categoryLabel = (category) => category || "Uncategorized";

export default function FoodDatabase() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const isArabic = lang === "ar";
  const guestMode =
    typeof window !== "undefined" &&
    localStorage.getItem("guestMode") === "true";
  const [foods, setFoods] = useState([]);
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [updatingFoodId, setUpdatingFoodId] = useState(null);

  const displayFoodName = (food) => localizeFoodName(food.name, lang);
  const displayCategory = (value) => localizeCategory(value, lang);
  const displayServing = (serving) => localizeServing(serving, lang);

  const loadFoods = useCallback(async () => {
    const { data, error } = await supabase.from("foods").select("*");
    if (error) {
      toast.error(formatApiError(error));
      return;
    }
    setFoods(data || []);
  }, []);

  const loadLogs = useCallback(async () => {
    if (guestMode) {
      setLogs(readGuestFoodLogs().filter((log) => logDate(log) === today()));
      return;
    }

    if (!user?.id) return;
    const { start, end } = todayRange();
    const { data, error } = await supabase
      .from("food_logs")
      .select("id, food_id, food_name, category, serving_size, quantity, potassium_mg, phosphorus_mg, sodium_mg, protein_g, logged_at")
      .eq("user_id", user.id)
      .gte("logged_at", start)
      .lt("logged_at", end)
      .order("logged_at", { ascending: false });

    if (error) {
      toast.error(formatApiError(error));
      return;
    }

    setLogs(data || []);
  }, [guestMode, user?.id]);

  useEffect(() => {
    loadFoods();
  }, [loadFoods]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const categories = useMemo(() => {
    return [...new Set(foods.map((food) => categoryLabel(food.category)))].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [foods]);

  const todayLogs = useMemo(() => {
    return logs.filter((log) => logDate(log) === today());
  }, [logs]);

  const servingCounts = useMemo(() => {
    return logs.reduce((acc, log) => {
      if (!log.food_id) return acc;
      const key = log.food_id;
      acc[key] = (acc[key] || 0) + Number(log.quantity || 0);
      return acc;
    }, {});
  }, [logs]);

  const filteredFoods = useMemo(() => {
    const query = search.trim().toLowerCase();

    return foods.filter((food) => {
      const localizedName = food.name || "";
      const serving = food.serving_size || "";
      const matchesSearch = !query || `${localizedName} ${serving}`.toLowerCase().includes(query);
      const matchesCategory = category === "all" || categoryLabel(food.category) === category;

      return matchesSearch && matchesCategory;
    });
  }, [category, foods, search]);

  const tableColumns = [
    { key: "food", label: isArabic ? "الصنف الغذائي" : "Food Item", width: "30%", align: isArabic ? "text-right" : "text-left" },
    { key: "serving", label: isArabic ? "الحصة" : "Serving", width: "13%", align: isArabic ? "text-right" : "text-left" },
    { key: "potassium", label: isArabic ? "البوتاسيوم" : "Potassium", width: "12%", align: "text-center" },
    { key: "phosphorus", label: isArabic ? "الفوسفور" : "Phosphorus", width: "13%", align: "text-center" },
    { key: "sodium", label: isArabic ? "الصوديوم" : "Sodium", width: "11%", align: "text-center" },
    { key: "protein", label: isArabic ? "البروتين" : "Protein", width: "10%", align: "text-center" },
    { key: "action", label: isArabic ? "الإجراء" : "Action", width: "11%", align: "text-center" },
  ];
  const tableColumnByKey = Object.fromEntries(tableColumns.map((column) => [column.key, column]));

  const addServing = async (foodId) => {
    const food = foods.find((item) => item.id === foodId);
    setUpdatingFoodId(foodId);

    if (guestMode) {
      const allLogs = readGuestFoodLogs();
      const existingLog = allLogs.find((log) => log.food_id === foodId && logDate(log) === today());
      const quantity = numberValue(existingLog?.quantity || 0) + 1;
      const nextLogs = existingLog
        ? allLogs.map((log) =>
            log.id === existingLog.id
              ? {
                  ...log,
                  quantity,
                  ...nutrientPayload(food, quantity),
                }
              : log
          )
        : [
            {
              id: crypto.randomUUID(),
              food_id: foodId,
              food_name: food?.name || "",
              category: food?.category || "",
              serving_size: food?.serving_size || "",
              quantity: 1,
              ...nutrientPayload(food, 1),
              logged_at: new Date().toISOString(),
            },
            ...allLogs,
          ];

      saveGuestFoodLogs(nextLogs);
      setLogs(nextLogs.filter((log) => logDate(log) === today()));
      setUpdatingFoodId(null);
      return;
    }

    if (!user?.id) {
      setUpdatingFoodId(null);
      return;
    }

    const existingLog = todayLogs.find((log) => log.food_id === foodId);

    try {
      const quantity = numberValue(existingLog?.quantity || 0) + 1;

      if (existingLog) {
        const { error } = await supabase
          .from("food_logs")
          .update({
            quantity,
            ...nutrientPayload(food, quantity),
          })
          .eq("id", existingLog.id)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("food_logs").insert({
            user_id: user.id,
            food_id: foodId,
            food_name: food?.name || "",
            category: food?.category || "",
            serving_size: food?.serving_size || "",
            quantity: 1,
            ...nutrientPayload(food, 1),
            logged_at: new Date().toISOString(),
          });
        if (error) throw error;
      }

      await loadLogs();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUpdatingFoodId(null);
    }
  };

  const removeServing = async (foodId) => {
    setUpdatingFoodId(foodId);

    if (guestMode) {
      const allLogs = readGuestFoodLogs();
      const foodLogs = allLogs.filter((log) => log.food_id === foodId && logDate(log) === today());
      const existingLog = foodLogs[0];

      if (!existingLog) {
        setUpdatingFoodId(null);
        return;
      }

      const quantity = numberValue(existingLog.quantity || 1);
      const nextQuantity = Math.max(0, quantity - 1);
      const food = foods.find((item) => item.id === foodId);
      const idsToDelete = foodLogs.map((log) => log.id).filter(Boolean);
      const nextLogs = nextQuantity > 0
        ? allLogs.map((log) =>
            log.id === existingLog.id
              ? {
                  ...log,
                  quantity: nextQuantity,
                  ...nutrientPayload(food, nextQuantity),
                }
              : log
          )
        : allLogs.filter((log) => !idsToDelete.includes(log.id));

      saveGuestFoodLogs(nextLogs);
      setLogs(nextLogs.filter((log) => logDate(log) === today()));
      setUpdatingFoodId(null);
      return;
    }

    if (!user?.id) {
      setUpdatingFoodId(null);
      return;
    }

    const foodLogs = todayLogs.filter((log) => log.food_id === foodId);
    const existingLog = foodLogs[0];
    if (!existingLog) {
      setUpdatingFoodId(null);
      return;
    }

    try {
      const quantity = numberValue(existingLog.quantity || 1);
      const nextQuantity = Math.max(0, quantity - 1);
      const food = foods.find((item) => item.id === foodId);

      if (nextQuantity > 0) {
        const { error } = await supabase
          .from("food_logs")
          .update({
            quantity: nextQuantity,
                ...nutrientPayload(food, nextQuantity),
              })
              .eq("id", existingLog.id)
              .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const idsToDelete = foodLogs.map((log) => log.id).filter(Boolean);
        const { error } = await supabase
          .from("food_logs")
          .delete()
          .in("id", idsToDelete)
          .eq("user_id", user.id);
        if (error) throw error;
        setLogs((current) => current.filter((log) => !idsToDelete.includes(log.id)));
      }

      await loadLogs();
    } catch (error) {
      console.error("removeServing error", error);
      toast.error(formatApiError(error));
    } finally {
      setUpdatingFoodId(null);
    }
  };

  const nutrientCell = (food, key, label, unit, limit) => {
    const value = nutrientValue(food, key);
    return (
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground md:hidden">
          {label}
        </div>
        <span
          className={`inline-flex min-w-[56px] items-baseline justify-center rounded-md border px-2 py-1 text-sm font-semibold md:min-w-0 md:justify-start md:rounded-none md:border-0 md:bg-transparent md:px-0 md:py-0 ${riskClass(
            value,
            limit
          )}`}
        >
          {value}
          <span className="ml-1 text-[11px] font-medium text-muted-foreground">{unit}</span>
        </span>
      </div>
    );
  };

  const desktopCell = (column, food, count, disabled) => {
    const cellClass = `px-4 py-3 align-middle ${column.align}`;

    if (column.key === "food") {
      return (
        <td key={column.key} className={cellClass}>
          <div className="truncate text-sm font-semibold text-foreground md:text-base">
            {displayFoodName(food)}
          </div>
        </td>
      );
    }

    if (column.key === "serving") {
      return (
        <td key={column.key} className={`${cellClass} text-sm font-medium text-muted-foreground`}>
          {food.serving_size ? displayServing(food.serving_size) : t("not_listed")}
        </td>
      );
    }

    if (column.key === "action") {
      return (
        <td key={column.key} className={cellClass}>
          <div className="flex h-8 items-center justify-center">
            {count > 0 ? (
              <div className="inline-flex h-8 items-center overflow-hidden rounded-md border border-border bg-background">
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 rounded-none ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                  onClick={() => removeServing(food.id)}
                  disabled={disabled}
                  aria-label={t("delete")}
                  data-testid={`remove-serving-${food.id}`}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="min-w-8 px-2 text-center text-sm font-semibold">{count}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 rounded-none ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                  onClick={() => addServing(food.id)}
                  disabled={disabled}
                  aria-label={t("add")}
                  data-testid={`add-serving-${food.id}`}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="icon"
                className={`h-8 w-8 rounded-md ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                onClick={() => addServing(food.id)}
                disabled={disabled}
                aria-label={t("add")}
                data-testid={`add-serving-${food.id}`}
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
        </td>
      );
    }

    const nutrientKeys = {
      potassium: "potassium_mg",
      phosphorus: "phosphorus_mg",
      sodium: "sodium_mg",
      protein: "protein_g",
    };
    const unit = column.key === "protein" ? "g" : "mg";

    return (
      <td key={column.key} className={`${cellClass} text-sm font-semibold text-foreground`}>
        {nutrientValue(food, nutrientKeys[column.key])}
        <span className="ml-1 text-[11px] font-medium text-muted-foreground">{unit}</span>
      </td>
    );
  };

  return (
    <div className="space-y-4" data-testid="food-database-root">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight md:text-3xl">
          {t("nav_foods")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isArabic
            ? `${filteredFoods.length} صنفًا غذائيًا متاحًا لتسجيل تغذية غسيل الكلى بسرعة`
            : `${filteredFoods.length} foods available for quick dialysis nutrition logging`}
        </p>
      </div>

      <div className="sticky top-0 z-20 rounded-lg border border-border bg-background/95 p-3 shadow-sm backdrop-blur">
        <div className="grid gap-3 md:grid-cols-[1fr_240px]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={isArabic ? "البحث عن طعام..." : t("search_food")}
              className="h-11 rounded-lg bg-card pl-9"
              data-testid="food-search"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-11 rounded-lg bg-card" data-testid="food-category-filter">
              <SelectValue placeholder={t("category")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_categories")}</SelectItem>
              {categories.map((item) => (
                <SelectItem key={item} value={item}>
                  {displayCategory(item)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {filteredFoods.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            {t("no_food_matches")}
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              <table dir={isArabic ? "rtl" : "ltr"} className="w-full table-fixed border-collapse text-sm">
                <colgroup>
                  {tableColumns.map((column) => (
                    <col key={column.key} style={{ width: column.width }} />
                  ))}
                </colgroup>
                <thead>
                  <tr className="border-b border-border bg-muted/60 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    {tableColumns.map((column) => (
                      <th key={column.key} className={`px-4 py-3 ${column.align}`}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredFoods.map((food) => {
                    const count = servingCounts[food.id] || 0;
                    const disabled = updatingFoodId === food.id;

                    return (
                      <tr
                        key={food.id}
                        className="border-b border-border/70 text-foreground transition-colors last:border-b-0 hover:bg-muted/40"
                        data-testid={`food-row-${food.id}`}
                      >
                        {tableColumns.map((column) => desktopCell(column, food, count, disabled))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="divide-y divide-border md:hidden">
              {filteredFoods.map((food) => {
                const count = servingCounts[food.id] || 0;
                const disabled = updatingFoodId === food.id;

                return (
                  <div
                    key={food.id}
                    className="grid gap-3 px-4 py-3 transition-colors hover:bg-muted/30"
                    data-testid={`food-row-mobile-${food.id}`}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-foreground">
                        {displayFoodName(food)}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {food.serving_size ? displayServing(food.serving_size) : t("serving_not_listed")}
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      {nutrientCell(food, "potassium_mg", "Potassium", "mg", DAILY_LIMITS.potassium)}
                      {nutrientCell(food, "phosphorus_mg", "Phosphorus", "mg", DAILY_LIMITS.phosphorus)}
                      {nutrientCell(food, "sodium_mg", "Sodium", "mg", DAILY_LIMITS.sodium)}
                      {nutrientCell(food, "protein_g", tableColumnByKey.protein.label, "g", null)}
                    </div>

                    <div className="flex h-8 items-center justify-start">
                      {count > 0 ? (
                        <div className="inline-flex h-8 items-center overflow-hidden rounded-md border border-border bg-background">
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 rounded-none ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                            onClick={() => removeServing(food.id)}
                            disabled={disabled}
                            aria-label={t("delete")}
                            data-testid={`remove-serving-mobile-${food.id}`}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="min-w-8 px-2 text-center text-sm font-semibold">{count}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 rounded-none ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                            onClick={() => addServing(food.id)}
                            disabled={disabled}
                            aria-label={t("add")}
                            data-testid={`add-serving-mobile-${food.id}`}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="icon"
                          className={`h-8 w-8 rounded-md ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                          onClick={() => addServing(food.id)}
                          disabled={disabled}
                          aria-label={t("add")}
                          data-testid={`add-serving-mobile-${food.id}`}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
